import React, { Component } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import Axios from 'axios';
import { injectStateService } from '@/shared/services/StateService';

import moment from 'moment';
import intlShape from '@/shared/utils/intlShape';
import SimplePanel from '@/shared/components/SimplePanel';
import { ColumnHeader, ScrollableTable } from '@/shared/components/table';
import api from '@/shared/api';
import PropTypes from 'prop-types';
import SignalRService from '@/shared/services/SignalRService';
import DataCacheService from '@/shared/services/DataCacheService';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import config from '../../config';
import NotificationRow from './NotificationRow';
import messages from './notificationMessages';
import NotificationFilters from './NotificationFilters';
import TaskDetailsModal from '../tasks/taskDetails/TaskDetailsModal';

class NotificationOverviewContainer extends Component {
  state = {
    items: [],
    initialSearchParams: {
      filter: {},
      page: 0,
      pageSize: 50,
      notificationFilters: {
        createDateFrom: moment.utc().startOf('month'),
        createDateTo: moment.utc().endOf('month'),
        period: 'month',
        notificationTypes: [],
        includeExpired: true,
        sharedWithMe: false,
      },
    },
    taskId: null,
    users: [],
  };

  constructor(props) {
    super(props);
    const { initialSearchParams: orgSearchParams } = this.state;
    const params = props.searchParams
      ? { notificationFilters: { ...orgSearchParams.notificationFilters, ...props.searchParams } }
      : JSON.parse(sessionStorage.getItem('NotificationOverviewParams'));
    if (!params) return;
    if (params.notificationFilters) {
      if (params.notificationFilters.createDateFrom) {
        params.notificationFilters.createDateFrom = moment(
          params.notificationFilters.createDateFrom,
        );
      }
      if (params.notificationFilters.createDateTo) {
        params.notificationFilters.createDateTo = moment(params.notificationFilters.createDateTo);
      }
    }

    this.state.initialSearchParams = {
      ...orgSearchParams,
      ...params,
    };
  }

  reloadNotificationsEventHandler = () => {
    this.setState(state => ({
      ...state,
      initialSearchParams: {
        ...state.currentSearchParams,
        // forces refresh of table
        random: new Date().getTime(),
      },
    }));
  };

  componentDidMount() {
    SignalRService.subscribe('reloadNotifications', this.reloadNotificationsEventHandler);
    const users = DataCacheService.getUsers();
    this.setState({ users });
  }

  componentWillUnmount() {
    SignalRService.unsubscribe('reloadNotifications', this.reloadNotificationsEventHandler);
    if (this.cancelToken && !this.cancelToken.token.reason) {
      this.cancelToken.cancel('Operation canceled due to leaving page.');
    }
  }

  static propTypes = {
    intl: intlShape.isRequired,
  };

  loadItems = searchParams => {
    const endpoint = `${config.appUrls.notifications}/api/notifications/overview`;

    sessionStorage.setItem(
      'NotificationOverviewParams',
      JSON.stringify({
        notificationFilters: searchParams.notificationFilters,
      }),
    );
    if (this.cancelToken && !this.cancelToken.token.reason) {
      this.cancelToken.cancel('Operation canceled due to new request.');
    }
    this.cancelToken = Axios.CancelToken.source();

    return api.base
      .post(
        endpoint,
        {
          ...searchParams.notificationFilters,
          page: searchParams.page,
          take: searchParams.pageSize,
        },
        {
          cancelToken: this.cancelToken.token,
        },
      )
      .then(({ data }) => {
        const notifications = this.mapNotifications(data.notifications);
        this.setState(state => ({
          ...state,
          items: searchParams.page > 0 ? [...state.items, ...notifications] : [...notifications],
          total: data.total,
          currentSearchParams: searchParams,
        }));        
        return data.notifications;
      })
      .catch(error => {
        if (!Axios.isCancel(error)) return error;
        // Return empty array for cancelled requests so ScrollableTable can handle it properly
        return [];
      });
  };

  mapNotifications(rows) {
    const { intl } = this.props;
    const notifications = rows.map(x => {
      const value = {
        ...x,
        params: x.json && typeof x.json === 'string' ? JSON.parse(x.json) || {} : {},
      };

      if (value.type === 208 || value.type === 209 || value.type === 227 || value.type === 228) {
        if (value.params.expiryDate) {
          const date = intl.formatDate(moment(value.params.expiryDate));
          value.params.expiryDate = date;
        }
        if ((value.type === 208 || value.type === 209) && !value.params.companyOwnerName) {
          value.params.companyOwnerName = value.params.name;
        }          
      }

      if (value.type === 700 && value.params.jobType) {
        value.params.jobType = intl.formatMessage(listItemMsg('jobType', value.params.jobType));
      }
      if (value.relation) {
        value.params.relation = value.relation.name;
      }
      return value;
    });

    return notifications;
  }

  filtersChanged = filters => {
    this.setState(state => ({
      ...state,
      initialSearchParams: {
        ...state.initialSearchParams,
        page: 0,
        notificationFilters: filters,
      },
    }));
  };

  markRead = notification => {
    api.base
      .post(`${config.appUrls.notifications}/api/notifications/markseen`, [notification.id])
      .then(() => {
        this.setState(state => ({
          ...state,
          items: state.items.map(x => {
            if (x.id === notification.id) {
              return {
                ...x,
                unread: false,
              };
            }
            return {
              ...x,
            };
          }),
        }));
      });
  };

  markExpired = notification => {
    api.base
      .post(`${config.appUrls.notifications}/api/notifications/expire`, [notification.id])
      .then(() => {
        const { state } = this;

        if (state.initialSearchParams.notificationFilters.includeExpired) {
          this.setState(state2 => ({
            ...state2,
            items: state2.items.map(x => {
              if (x.id === notification.id) {
                return {
                  ...x,
                  expireDate: moment(),
                };
              }
              return {
                ...x,
              };
            }),
          }));
        } else {
          this.setState(state2 => ({
            ...state2,
            items: state2.items.filter(x => x.id !== notification.id),
          }));
        }
      });
  };
  openSettings = e => {
    const { stateService } = this.props;
    e.preventDefault();
    stateService.gotoState('notificationSettings');
  };
  renderRow = item => (
    <NotificationRow
      key={item.id}
      notification={item}
      markRead={() => this.markRead(item)}
      markExpired={() => this.markExpired(item)}
      openTaskDialog={taskId => this.setState({ taskId })}
    />
  );

  render() {
    const { intl } = this.props;
    const { initialSearchParams, items, total, taskId, users } = this.state;

    return (
      <div>
        <SimplePanel
          title={intl.formatMessage(messages.Notifications)}
          buttons={
            <a
              href="/#/notificationSettings"
              className="pull-right white"
              onClick={this.openSettings}
            >
              <i className="fa fa-cogs" />
              <span className="margin5L hidden-xs">
                <FormattedMessage {...messages.OpenNotificationSettings} />
              </span>
            </a>
          }
        >
          <NotificationFilters
            filters={initialSearchParams.notificationFilters}
            onFilterChange={filters => this.filtersChanged(filters)}
            total={total}
          />
        </SimplePanel>

        <ScrollableTable
          tableClassName="table-grid margin50B"
          loadItems={searchParams => this.loadItems(searchParams)}
          initialSearchParams={initialSearchParams}
          items={items}
          renderRow={item => this.renderRow(item)}
        >
          <ColumnHeader title={intl.formatMessage(messages.CreatedOn)} className="col-xs-1" />
          <ColumnHeader title={intl.formatMessage(messages.Relation)} className="col-xs-2" />
          <ColumnHeader title={intl.formatMessage(messages.Message)} className="col-xs-8" />
          <ColumnHeader className="col-xs-2 text-right" />
        </ScrollableTable>
        {taskId && (
          <TaskDetailsModal
            show
            task={{ id: taskId }}
            users={users}
            onSave={() => this.setState({ taskId: null })}
            onClose={() => this.setState({ taskId: null })}
          />
        )}
      </div>
    );
  }
}

export default injectStateService(injectIntl(NotificationOverviewContainer));
